import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ABOUT } from "../constants/copy";

/* ================== ENV ================== */
const GITHUB_USERNAME = process.env.REACT_APP_GITHUB_USERNAME;
const REPO_NAME = process.env.REACT_APP_GITHUB_REPO;
const BRANCH = process.env.REACT_APP_GITHUB_BRANCH;
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;

/* ================== AXIOS ================== */
const githubApi = axios.create({
  baseURL: "https://api.github.com",
  headers: {
    Authorization: `Bearer ${GITHUB_TOKEN}`,
    Accept: "application/vnd.github+json",
  },
});

/* ================== SKELETON ================== */
const Skeleton = () => (
  <div className="w-full h-full rounded-2xl bg-white/10 animate-pulse" />
);

export default function Photography() {
  const [featured, setFeatured] = useState([]);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);

  /* 🔥 LIGHTBOX STATE */
  const [lightbox, setLightbox] = useState({
    open: false,
    images: [],
    index: 0,
  });

  /* ⚡ Memoized RAW builder (prevents recreation on every render) */
  const raw = useMemo(
    () => (path) =>
      `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/${path}`,
    []
  );

  /* ================== FETCH DATA ================== */
  useEffect(() => {
    const fetchPhotography = async () => {
      try {
        const cached = localStorage.getItem("photography");
        if (cached) {
          const data = JSON.parse(cached);
          setFeatured(data.featured);
          setMoments(data.moments);
          setLoading(false);
          return;
        }

        const [featuredRes, momentsRes] = await Promise.all([
          githubApi.get(
            `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/photography/featured`
          ),
          githubApi.get(
            `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/photography/moments`
          ),
        ]);

        const mapImages = (res, folder) =>
          res.data
            .filter((i) => i.type === "file")
            .map((i) => ({
              src: raw(`photography/${folder}/${i.name}`),
              alt: i.name.replace(/[-_]/g, " ").split(".")[0],
            }));

        const data = {
          featured: mapImages(featuredRes, "featured"),
          moments: mapImages(momentsRes, "moments"),
        };

        setFeatured(data.featured);
        setMoments(data.moments);
        localStorage.setItem("photography", JSON.stringify(data));
      } catch (err) {
        console.error("Failed to load photography images", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotography();
  }, [raw]);

  /* ================== LIGHTBOX CONTROLS ================== */
  useEffect(() => {
    if (!lightbox.open) return;

    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [lightbox.open, lightbox.index]);

  const openLightbox = (images, index) =>
    setLightbox({ open: true, images, index });

  const closeLightbox = () =>
    setLightbox({ open: false, images: [], index: 0 });

  const nextImage = () =>
    setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length }));

  const prevImage = () =>
    setLightbox((s) => ({
      ...s,
      index: (s.index - 1 + s.images.length) % s.images.length,
    }));

  /* ================== RENDER ================== */
  return (
    <section className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-10 sm:py-14 md:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto">
        <h1 className="section-heading mb-3 sm:mb-4">Photography</h1>
        <p className="section-sub mb-8 sm:mb-12">{ABOUT.main}</p>

        <div className="mb-10 sm:mb-14">
          <Link to="/gallery" className="btn-primary">
            View full gallery
          </Link>
        </div>

        {/* FEATURED */}
        <h2 className="font-inria font-light text-xl sm:text-2xl md:text-3xl text-weddmac-gold/90 mb-6 tracking-wide">
          Featured work
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[220px] md:auto-rows-[260px] gap-3 md:gap-4 mb-16">
          {(loading ? Array.from({ length: 6 }) : featured).map((item, i) => {
            const layout =
              i === 0
                ? "md:col-span-2 md:row-span-2"
                : i === 2
                ? "md:row-span-2"
                : i === 4
                ? "md:col-span-2"
                : "";

            return (
              <div
                key={i}
                className={`group relative overflow-hidden rounded-2xl shadow-card ${layout}`}
                onClick={() => openLightbox(featured, i)}
              >
                {loading ? (
                  <Skeleton />
                ) : (
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading={i < 2 ? "eager" : "lazy"} // ⚡ preload only first 2
                    className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300 group-hover:scale-105"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* MOMENTS */}
        <h2 className="font-inria font-light text-xl sm:text-2xl md:text-3xl text-weddmac-gold/90 mb-6 tracking-wide">
          Moments
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] md:auto-rows-[240px] gap-3 md:gap-4">
          {(loading ? Array.from({ length: 8 }) : moments).map((item, i) => {
            const layout =
              i % 7 === 0
                ? "md:col-span-2 md:row-span-2"
                : i % 5 === 0
                ? "md:row-span-2"
                : "";

            return (
              <div
                key={i}
                className={`group relative overflow-hidden rounded-2xl shadow-card ${layout}`}
                onClick={() => openLightbox(moments, i)}
              >
                {loading ? (
                  <Skeleton />
                ) : (
                  <img
                    src={item.src}
                    alt={item.alt}
                    // loading="lazy" // ⚡ lazy load all gallery images
                    className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300 group-hover:scale-105"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* LIGHTBOX */}
      {lightbox.open && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <img
            src={lightbox.images[lightbox.index].src}
            alt={lightbox.images[lightbox.index].alt}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}